import React, { useEffect, useState } from "react";
import {
  Box, Typography, Chip, Stack, Paper, Skeleton, Button, Divider,
} from "@mui/material";
import AnchorIcon from "@mui/icons-material/Anchor";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import ShareButton from "../components/common/ShareButton";
import ImageSlider from "../components/common/ImageSlider";
import SiteHeader from "../components/layout/SiteHeader";
import { fetchYachtDetails } from "../store/yachtSlice";
import { AppDispatch } from "../store/store";

const BACKEND_URL = process.env.REACT_APP_API_URL || '';

const proxyImg = (url: string, w: number, h: number) =>
  `${BACKEND_URL}/api/image/?url=${encodeURIComponent(url)}&w=${w}&h=${h}&q=82`;

interface ItineraryDay {
  day: string;
  from: string;
  to: string;
  nm: number;
  highlight: string;
}

interface Props {
  payload: {
    yachtId: number | string;
    yachtName: string;
    kind: string;
    images: { url: string; description?: string }[];
    dateFrom: string;
    dateTo: string;
    startBase?: string;
    country?: string;
  };
  code: string;
}

const SPEC_STYLES: Record<string, { bg: string; color: string; border: string }> = {
  cabins:  { bg: '#e8f0fe', color: '#1a56db', border: '#c3d7fd' },
  guests:  { bg: '#e6f4ea', color: '#137333', border: '#b7dfbe' },
  wc:      { bg: '#fff3e0', color: '#e65100', border: '#ffcc80' },
  year:    { bg: '#fce8e6', color: '#b91c1c', border: '#f5c6c3' },
  length:  { bg: '#f3e8fd', color: '#6b21a8', border: '#ddb8f9' },
  engine:  { bg: '#e0f2f1', color: '#00695c', border: '#99d6cf' },
};

const SailingPlanPage: React.FC<Props> = ({ payload, code }) => {
  const dispatch = useDispatch<AppDispatch>();
  const { yacht: yachtState } = useSelector((state: any) => state.yacht);
  const [itinerary, setItinerary] = useState<ItineraryDay[]>([]);
  const [itineraryLoading, setItineraryLoading] = useState(true);
  const [showAllEquipment, setShowAllEquipment] = useState(false);

  const { yachtId, yachtName, kind, images, dateFrom, dateTo, startBase, country } = payload;

  const baseName = (startBase || '').split('/')?.[0]?.trim();
  const nights = moment(dateTo).diff(moment(dateFrom), 'days');
  const dateFromFmt = moment(dateFrom).format('D MMM YYYY');
  const dateToFmt = moment(dateTo).format('D MMM YYYY');

  const fullYacht = String(yachtState?.yachtId) === String(yachtId) ? yachtState : null;
  const equipment: { name: string; value: string }[] = fullYacht?.yacht?.equipment_json || [];
  const rawSpecs = fullYacht ? {
    cabins: fullYacht.yacht?.cabins,
    guests: fullYacht.yacht?.max_people_on_board,
    wc: fullYacht.yacht?.wc,
    year: fullYacht.yacht?.year,
    length: fullYacht.yacht?.length ? `${Math.round(fullYacht.yacht.length * 3.281)} ft` : null,
    engine: fullYacht.yacht?.engine,
  } : null;

  const specItems = rawSpecs ? [
    { key: 'cabins',  label: 'Cabins',  value: rawSpecs.cabins  ? `${rawSpecs.cabins} cabins`  : null },
    { key: 'guests',  label: 'Guests',  value: rawSpecs.guests  ? `${rawSpecs.guests} guests`  : null },
    { key: 'wc',      label: 'WC',      value: rawSpecs.wc      ? `${rawSpecs.wc} WC`          : null },
    { key: 'year',    label: 'Built',   value: rawSpecs.year    ? `Built ${rawSpecs.year}`      : null },
    { key: 'length',  label: 'Length',  value: rawSpecs.length  || null },
    { key: 'engine',  label: 'Engine',  value: rawSpecs.engine  || null },
  ].filter(s => s.value) : [];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (!fullYacht && yachtId) {
      dispatch(fetchYachtDetails({
        yachtId: String(yachtId),
        date_from: dateFrom,
        date_to: dateTo,
        country: country || '',
      }));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [yachtId]);

  useEffect(() => {
    if (!baseName) { setItineraryLoading(false); return; }
    fetch(`${BACKEND_URL}/yachts/itinerary/?base=${encodeURIComponent(baseName)}&country=${encodeURIComponent(country || '')}&days=${nights || 7}`)
      .then(r => r.json())
      .then(d => setItinerary(d.days || []))
      .catch(() => {})
      .finally(() => setItineraryLoading(false));
  }, [baseName, country, nights]);

  const imageUrls = images.map(img => proxyImg(img.url, 800, 500));
  const visibleEquipment = showAllEquipment ? equipment : equipment.slice(0, 12);

  const shareUrl = `${window.location.origin}/s/${code}`;
  const ogTitle = `${yachtName} ${kind} — SailArmada`;
  const ogImage = images?.[0]?.url ? proxyImg(images[0].url, 1200, 630) : undefined;

  return (
    <Box>
      <SiteHeader />

      <Box sx={{ maxWidth: 680, mx: 'auto', pb: 6, mt: 'calc(var(--banner-height, 0px) + var(--nav-height, 68px))' }}>
        {/* Title bar */}
        <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid #eee', textAlign: 'center' }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            You are one step closer to your sailing week!
          </Typography>
        </Box>

        {/* Image slider */}
        {imageUrls.length > 0 && (
          <Box sx={{ px: 0 }}>
            <ImageSlider images={imageUrls} />
          </Box>
        )}

        <Box sx={{ px: 2 }}>
          {/* Heading */}
          <Typography variant="h6" fontWeight={700} sx={{ mt: 1.5 }}>
            {yachtName} {kind}
          </Typography>
          {baseName && (
            <Typography variant="body2" color="text.secondary">
              {baseName}{country ? `, ${country}` : ''}
            </Typography>
          )}
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            {dateFromFmt} → {dateToFmt} · {nights} night{nights !== 1 ? 's' : ''}
          </Typography>

          {/* Share button */}
          <ShareButton
            url={shareUrl}
            label="Share with your crew"
            og={{ og_title: ogTitle, og_image: ogImage, og_description: `${dateFromFmt} → ${dateToFmt} · ${baseName}${country ? ', ' + country : ''}` }}
          />

          <Divider sx={{ my: 2 }} />

          {/* Specs */}
          {specItems.length > 0 && (
            <>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>Boat Specs</Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1, mb: 2 }}>
                {specItems.map(({ key, label, value }) => {
                  const style = SPEC_STYLES[key] || SPEC_STYLES.cabins;
                  return (
                    <Box key={key} sx={{
                      p: 1.25,
                      borderRadius: 1.5,
                      bgcolor: style.bg,
                      border: `1px solid ${style.border}`,
                      textAlign: 'center',
                    }}>
                      <Typography variant="body2" fontWeight={700} sx={{ color: style.color, lineHeight: 1.3 }}>
                        {value}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            </>
          )}

          {/* Equipment */}
          {equipment.length > 0 && (
            <>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>Equipment</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 1 }}>
                {visibleEquipment.map((eq, i) => (
                  <Chip
                    key={i}
                    size="small"
                    label={eq.name}
                    sx={{ bgcolor: '#e0f2f1', color: '#00695c', fontWeight: 500, border: '1px solid #99d6cf' }}
                  />
                ))}
              </Box>
              {equipment.length > 12 && (
                <Button size="small" sx={{ textTransform: 'none', color: '#1a1a2e', p: 0, mb: 2 }} onClick={() => setShowAllEquipment(v => !v)}>
                  {showAllEquipment ? 'Show less ↑' : `+ ${equipment.length - 12} more ↓`}
                </Button>
              )}
              {equipment.length <= 12 && <Box mb={2} />}
            </>
          )}

          <Divider sx={{ my: 2 }} />

          {/* Itinerary */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
            <AnchorIcon sx={{ fontSize: 18, color: '#1a1a2e' }} />
            <Typography variant="subtitle2" fontWeight={600}>Proposed Sailing Itinerary</Typography>
          </Box>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
            Route from {baseName || 'your base'} — for inspiration only.
          </Typography>

          <Stack spacing={1}>
            {itineraryLoading
              ? Array.from({ length: 7 }).map((_, i) => (
                  <Skeleton key={i} variant="rounded" height={56} />
                ))
              : itinerary.length > 0
                ? itinerary.map((day) => (
                    <Paper key={day.day} variant="outlined" sx={{ px: 2, py: 1.25, borderRadius: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                        <Typography variant="caption" fontWeight={700} sx={{ color: '#1a1a2e', minWidth: 76 }}>
                          {day.day}
                        </Typography>
                        <Typography variant="body2" fontWeight={600} sx={{ flexGrow: 1 }}>
                          {day.from} → {day.to}
                          <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.75 }}>
                            ~{day.nm} nm
                          </Typography>
                        </Typography>
                      </Box>
                      <Typography variant="caption" color="text.secondary" sx={{ pl: '88px' }}>
                        {day.highlight}
                      </Typography>
                    </Paper>
                  ))
                : (
                    <Typography variant="body2" color="text.secondary">
                      Itinerary could not be generated — check back later.
                    </Typography>
                  )
            }
          </Stack>
        </Box>
      </Box>
    </Box>
  );
};

export default SailingPlanPage;
